import React from 'react';
import { Plus, Split, Star, Trash2, Users } from 'lucide-react';
import { Button } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { RecipientInsetFieldShell } from '../emails/components/RecipientSelector';
import ContactPointsEditor from './ContactPointsEditor';
import {
  emptyOrganizationContact,
  hasSplittableOrganizationContacts,
  normalizeOrganizationContactEmails,
  splitOrganizationContactsByEmail,
  type OrganizationContactRow,
} from './organizationContacts';
import { emptyContactPointRow, EMAIL_TYPE_OPTIONS, type ContactPointRow } from './contactPoints';
import { deleteActionClass, secondaryTextClass } from './themeHelpers';

interface OrganizationContactsEditorProps {
  value: OrganizationContactRow[];
  onChange: (contacts: OrganizationContactRow[]) => void;
  disabled?: boolean;
}

const OrganizationContactsEditor: React.FC<OrganizationContactsEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { colors } = useTheme();
  const contacts = value.length > 0 ? value : [emptyOrganizationContact(true)];

  const insetInputClassName = cn(
    'flex-1 min-w-0 w-full bg-transparent border-0 outline-none text-sm py-0.5',
    'placeholder-gray-400 dark:placeholder-gray-500',
    colors.text,
    'disabled:opacity-50 disabled:cursor-not-allowed',
  );

  const updateContacts = (nextContacts: OrganizationContactRow[]) => {
    onChange(nextContacts.length > 0 ? nextContacts : [emptyOrganizationContact(true)]);
  };

  const updateContact = (index: number, patch: Partial<OrganizationContactRow>) => {
    updateContacts(contacts.map((contact, contactIndex) => {
      if (contactIndex !== index) return contact;
      const next = { ...contact, ...patch };
      if (patch.emails) {
        next.emails = normalizeOrganizationContactEmails(patch.emails);
      }
      return next;
    }));
  };

  const removeContact = (index: number) => {
    updateContacts(contacts.filter((_, contactIndex) => contactIndex !== index));
  };

  const addContact = () => {
    updateContacts([...contacts, emptyOrganizationContact(false)]);
  };

  const addEmailToContact = (index: number) => {
    const emails = contacts[index].emails;
    updateContact(index, {
      emails: [...emails, emptyContactPointRow('EMAIL', emails.length === 0)],
    });
  };

  const splitAllContacts = () => {
    updateContacts(splitOrganizationContactsByEmail(contacts));
  };

  const canSplitAny = hasSplittableOrganizationContacts(contacts);

  const inlineFieldClassName = cn(
    'w-full min-w-0 rounded-md border transition-colors h-8 px-2 text-sm',
    'focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    colors.border,
    'bg-white dark:bg-gray-950/60',
    colors.text,
  );

  const updateContactEmail = (index: number, patch: Partial<ContactPointRow>) => {
    const email = contacts[index].emails[0] ?? emptyContactPointRow('EMAIL', true);
    updateContact(index, { emails: [{ ...email, ...patch, isPrimary: true }] });
  };

  const actionButtonClass = cn(
    'inline-flex h-8 shrink-0 items-center gap-1 rounded-md border px-2.5 text-xs font-medium transition-colors',
    colors.border,
    colors.textSecondary,
    'disabled:cursor-not-allowed disabled:opacity-50',
  );

  return (
    <div
      className={cn(
        'min-w-0 w-full overflow-hidden rounded-lg border',
        colors.border,
        'bg-white dark:bg-gray-950/20',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-2 border-b px-3 py-2',
          colors.border,
          'bg-indigo-50/60 dark:bg-indigo-950/15',
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Users className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden />
          <p className={cn('text-sm font-semibold', colors.text)}>Contacts</p>
          <span
            className={cn(
              'rounded-full px-1.5 py-px text-[10px] font-semibold',
              'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300',
            )}
          >
            {contacts.length}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {canSplitAny ? (
            <button
              type="button"
              onClick={splitAllContacts}
              disabled={disabled}
              title="Split every multi-email contact into separate contacts"
              className={cn(
                actionButtonClass,
                'h-7 px-2',
                'hover:border-amber-400 hover:bg-amber-50/60 hover:text-amber-800',
                'dark:hover:border-amber-700 dark:hover:bg-amber-950/20 dark:hover:text-amber-300',
              )}
            >
              <Split className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden sm:inline">Split all</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={addContact}
            disabled={disabled}
            className={cn(
              actionButtonClass,
              'h-7 px-2',
              'hover:border-emerald-400 hover:bg-emerald-50/60 hover:text-emerald-700',
              'dark:hover:border-emerald-700 dark:hover:bg-emerald-950/20 dark:hover:text-emerald-300',
            )}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add
          </button>
        </div>
      </div>

      <div className="divide-y">
        {contacts.map((contact, index) => {
          const singleEmailRow = contact.emails.length === 1;
          const email = contact.emails[0] ?? emptyContactPointRow('EMAIL', true);
          const showContactDelete = contacts.length > 1;

          const headerGrid = showContactDelete
            ? 'grid-cols-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]'
            : 'grid-cols-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]';

          const renderNamePosition = () => (
            <>
              <RecipientInsetFieldShell label="Name" colors={colors}>
                <input
                  type="text"
                  value={contact.contactName}
                  onChange={(event) => updateContact(index, { contactName: event.target.value })}
                  placeholder="Contact name"
                  disabled={disabled}
                  aria-label={`Contact ${index + 1} name`}
                  className={insetInputClassName}
                />
              </RecipientInsetFieldShell>
              <RecipientInsetFieldShell
                label="Position"
                labelWidthClass="w-[4.75rem]"
                colors={colors}
              >
                <input
                  type="text"
                  value={contact.contactPosition}
                  onChange={(event) => updateContact(index, { contactPosition: event.target.value })}
                  placeholder="Position"
                  disabled={disabled}
                  aria-label={`Contact ${index + 1} position`}
                  className={insetInputClassName}
                />
              </RecipientInsetFieldShell>
            </>
          );

          const renderEmailFields = () => (
            <>
              <select
                value={email.label}
                onChange={(event) => updateContactEmail(index, { label: event.target.value })}
                disabled={disabled}
                aria-label="Email type"
                className={cn(inlineFieldClassName, 'text-xs')}
              >
                {EMAIL_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                type="email"
                value={email.value}
                onChange={(event) => updateContactEmail(index, { value: event.target.value })}
                placeholder="name@example.com"
                disabled={disabled}
                aria-label={`Contact ${index + 1} email`}
                className={cn(
                  inlineFieldClassName,
                  email.isPrimary && 'border-indigo-300 dark:border-indigo-800',
                )}
              />
              <button
                type="button"
                onClick={() => updateContactEmail(index, { isPrimary: true })}
                disabled={disabled}
                className={cn(
                  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition-colors',
                  email.isPrimary
                    ? 'border-indigo-600 bg-indigo-600 text-white dark:border-indigo-500 dark:bg-indigo-600'
                    : cn(colors.border, colors.surface, secondaryTextClass(colors), 'hover:bg-gray-100 dark:hover:bg-gray-800'),
                )}
                title={email.isPrimary ? 'Primary email' : 'Set as primary'}
              >
                <Star className={cn('h-3.5 w-3.5', email.isPrimary ? 'fill-current' : '')} aria-hidden />
              </button>
            </>
          );

          const renderAddEmailButton = (compact = false) => (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => addEmailToContact(index)}
              disabled={disabled}
              title="Add email"
              aria-label="Add email"
              className={cn(
                'w-9 shrink-0',
                compact ? 'h-8' : 'h-8 sm:h-[2.25rem] sm:self-stretch',
                'text-indigo-600 hover:bg-indigo-50/60 hover:text-indigo-700',
                'dark:text-indigo-400 dark:hover:bg-indigo-950/20 dark:hover:text-indigo-300',
              )}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
            </Button>
          );

          const renderDeleteContactButton = (compact = false) => (
            showContactDelete ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeContact(index)}
                disabled={disabled}
                title="Remove contact"
                aria-label="Remove contact"
                className={cn(
                  'w-9 shrink-0',
                  compact ? 'h-8' : 'h-[2.25rem] self-stretch',
                  deleteActionClass(colors),
                )}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </Button>
            ) : null
          );

          return (
          <div key={contact.key} className="px-3 py-2 space-y-1.5">
            {singleEmailRow ? (
              <>
                <div className={cn('grid items-center gap-1.5 lg:hidden', headerGrid)}>
                  {renderNamePosition()}
                  {renderAddEmailButton()}
                  {renderDeleteContactButton()}
                </div>
                <div className="grid grid-cols-[4.25rem_minmax(0,1fr)_2rem] items-center gap-1.5 lg:hidden">
                  {renderEmailFields()}
                </div>
                <div
                  className={cn(
                    'hidden items-center gap-1.5 lg:grid',
                    showContactDelete
                      ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_4.25rem_minmax(0,1.5fr)_auto_auto_auto]'
                      : 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_4.25rem_minmax(0,1.5fr)_auto_auto]',
                  )}
                >
                  {renderNamePosition()}
                  {renderEmailFields()}
                  {renderAddEmailButton(true)}
                  {renderDeleteContactButton(true)}
                </div>
              </>
            ) : (
              <div className={cn('grid items-center gap-1.5', headerGrid)}>
                {renderNamePosition()}
                {renderAddEmailButton()}
                {renderDeleteContactButton()}
              </div>
            )}

            {!singleEmailRow ? (
            <ContactPointsEditor
              kind="EMAIL"
              label="Emails"
              value={contact.emails}
              onChange={(emails) => updateContact(index, { emails })}
              disabled={disabled}
              showPersonFields={false}
              embedded
              compact
              hideHeader
            />
            ) : null}
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrganizationContactsEditor;
